/**
 * Where a screen's scroller rests when nobody has scrolled it (IU-14, I-1278). That is 0 on most
 * screens, and the tuck on a phone list that opens with its search field hidden under the top row
 * (S-5, `PhoneSearchField`).
 *
 * Whoever sets a resting offset names it on the scroller itself, so anything that asks "has the
 * reader scrolled?" reads one answer and need not know why the screen rests where it does. The work
 * items list asked `scrollTop > 0` instead: tucked at 64 it took an untouched list for a scrolled
 * one, followed a row that regrouped a section down, and keep-position stored where it went.
 */
export const RESTING_SCROLL_TOP_ATTRIBUTE = 'data-resting-scroll-top';

/** A scroll short of this is the browser's rounding of an offset, not a reader's move. */
const SCROLLED_BY_PX = 1;

/** Names `top` as where `scroller` rests; returns the undo. */
export function nameRestingScrollTop(scroller: Element, top: number): () => void {
  scroller.setAttribute(RESTING_SCROLL_TOP_ATTRIBUTE, String(top));
  return () => scroller.removeAttribute(RESTING_SCROLL_TOP_ATTRIBUTE);
}

/** Where `scroller` rests: the offset named on it, else 0. */
export function restingScrollTopOf(scroller: Element | null): number {
  const named = Number(scroller?.getAttribute(RESTING_SCROLL_TOP_ATTRIBUTE));
  return Number.isFinite(named) && named > 0 ? named : 0;
}

/** Whether the reader has scrolled `scroller` past where it rests. */
export function scrolledPastRest(scroller: Element | null, scrollTop: number): boolean {
  return scrollTop - restingScrollTopOf(scroller) >= SCROLLED_BY_PX;
}

/**
 * The room `PhoneSearchField` keeps at the page's end so a short list can rest at its tuck. Whoever
 * watches the page's boxes for a change leaves this one out: it is resized from inside those very
 * callbacks, and a box resized by its own observer is a "ResizeObserver loop".
 */
export const RESTING_SPACER_SELECTOR = '[data-phone-search-spacer]';
